/**
 * Analytics Script Policy
 * ─────────────────────────────────────────────────────────────────
 * Hard runtime guard som blockerar OKÄNDA analytics-script (gtag.js,
 * gtm.js, GA legacy, Cloudflare Zaraz) innan de hinner exekvera.
 *
 * Tillåtna källor:
 *   • Vår officiella GTM-container (GTM-T8PVRK33) injicerad av
 *     `gtmBootstrap.ts` (matchas via data-attributet `data-gtm-container`).
 *   • GTM child-loaders för whitelistade GA4/Ads-mätningar
 *     (cx=c | gtm= | gtg_health=1) med container-ID i
 *     TRUSTED_CHILD_CONTAINER_IDS.
 *
 * Allt annat blockeras: `<script>`-elementet får sin `src` neutraliserad,
 * `type` ändras till `text/x-blocked-analytics`, och en `data-blocked-by`
 * sätts. Detta sker via en `MutationObserver` som körs FÖRE script-execution.
 *
 * Pure browser-kod, defensiv, kastar aldrig.
 */

import {
  OFFICIAL_GTM_CONTAINER_ID,
  TRUSTED_CHILD_CONTAINER_IDS,
} from "@/lib/gtmConstants";
import { isTrackingEnabled } from "@/lib/trackingEnvironment";
import { supabase } from "@/integrations/supabase/client";

declare global {
  interface Window {
    __zooiAnalyticsPolicyInstalled?: boolean;
    __zooiAnalyticsPolicyBlocks?: BlockedScript[];
  }
}

export type BlockedScript = {
  src: string;
  vendor: "gtag" | "gtm" | "zaraz" | "ga-legacy" | "tiktok" | "meta" | "clarity";
  containerId: string | null;
  reason: string;
  blockedAt: string;
};

const ALERT_LOCALSTORAGE_KEY = "zooi.analytics-policy.lastAlertHash";
const ALERT_COOLDOWN_MS = 6 * 60 * 60 * 1000; // 6h

/**
 * Consent-gated denylist. När `cookieConsent !== "accepted"` (vilket är
 * default i Consent Mode V2: ad_storage/analytics_storage = denied) ska
 * tredjepartspixlar ALDRIG köra — inte ens om en GTM-tagg råkar fyra utan
 * korrekt consent-trigger. Detta är vår sista försvarslinje för Consent
 * Mode V2 och håller dessutom LCP-fönstret rent från TikTok/Meta/Clarity-JS.
 */
const CONSENT_GATED_PIXEL_PATTERNS: Array<{ re: RegExp; vendor: BlockedScript["vendor"] }> = [
  { re: /(^|\.)analytics\.tiktok\.com|tiktok\.com\/i18n\/pixel/i, vendor: "tiktok" },
  { re: /(^|\.)connect\.facebook\.net|facebook\.com\/tr|fbevents\.js/i, vendor: "meta" },
  { re: /(^|\.)clarity\.ms/i, vendor: "clarity" },
];

function isConsentGranted(): boolean {
  try {
    return window.localStorage.getItem("cookieConsent") === "accepted";
  } catch {
    return false;
  }
}

function classifyAnalyticsSrc(src: string): BlockedScript["vendor"] | null {
  if (/googletagmanager\.com\/gtag\/js/.test(src)) return "gtag";
  if (/googletagmanager\.com\/gtm\.js/.test(src)) return "gtm";
  if (/google-analytics\.com\/(analytics|ga)\.js/.test(src)) return "ga-legacy";
  if (/cdn\.cloudflare(insights)?\.com\/zaraz/.test(src)) return "zaraz";
  return null;
}


function extractContainerId(src: string): string | null {
  const m = src.match(/[?&]id=([A-Z0-9-]+)/i);
  return m?.[1] ?? null;
}

/**
 * Returnerar `null` om scriptet är tillåtet, annars en mänsklig
 * blockerings-anledning.
 */
function evaluateScript(
  el: HTMLScriptElement,
): { vendor: BlockedScript["vendor"]; reason: string; containerId: string | null } | null {
  const src = el.src || el.getAttribute("src") || "";
  if (!src) return null;

  // ── Consent-gated pixel denylist ─────────────────────────────────
  // Blockerar TikTok/Meta/Clarity-script så länge användaren inte
  // explicit klickat "Acceptera alla". Detta är Consent Mode V2 enforcat
  // i DOM-lagret — om en GTM-tagg saknar `consent_type=ad_storage` trigger
  // och försöker fyra ändå så fångar vi den här innan JS körs. Bonus:
  // håller LCP-tråden ren från 3rd-party JS på första-sidans render.
  if (!isConsentGranted()) {
    for (const { re, vendor: pixelVendor } of CONSENT_GATED_PIXEL_PATTERNS) {
      if (re.test(src)) {
        return {
          vendor: pixelVendor,
          reason: `Consent Mode V2: ${pixelVendor}-pixel blockerad — cookieConsent !== "accepted".`,
          containerId: null,
        };
      }
    }
  }

  const vendor = classifyAnalyticsSrc(src);
  if (!vendor) return null;


  const containerId = extractContainerId(src);

  // Tillåt vår egen GTM-container
  if (vendor === "gtm" && containerId === OFFICIAL_GTM_CONTAINER_ID) return null;

  // Tillåt gtag.js för alla whitelistade container-ID:n oavsett laddningsväg.
  // Detta täcker BÅDE:
  //   (a) GTM child-loaders (cx=c | gtm= | gtg_health=), som GTM själv injicerar
  //   (b) VÅR EGEN inline-snippet i index.html som laddar
  //       googletagmanager.com/gtag/js?id=AW-18059753112 direkt (utan
  //       loader-params). Tidigare krävdes cx=c/gtm=/gtg_health= här vilket
  //       skapade en race condition: om policyn hann installera före async-
  //       scriptets execution blockerades vår egen Ads-tag intermittent.
  //       Container-ID:t är den auktoritativa allow-signalen — inte query-formen.
  const isAllowedGtag =
    vendor === "gtag" &&
    containerId !== null &&
    TRUSTED_CHILD_CONTAINER_IDS.has(containerId);
  if (isAllowedGtag) return null;

  // Inline-bootstrap har dataset.gtmContainer = vår officiella container
  if (el.dataset.gtmContainer === OFFICIAL_GTM_CONTAINER_ID) return null;

  let reason: string;
  if (vendor === "gtm") {
    reason = `Blockerad GTM-container (${containerId ?? "okänt id"}) — bara ${OFFICIAL_GTM_CONTAINER_ID} tillåts.`;
  } else if (vendor === "gtag") {
    reason = containerId
      ? `Blockerad gtag.js för ${containerId} — finns inte i TRUSTED_CHILD_CONTAINER_IDS.`
      : "Blockerad gtag.js utan giltigt container-id.";
  } else if (vendor === "ga-legacy") {
    reason = "Blockerad legacy Universal Analytics-script (analytics.js/ga.js).";
  } else {
    reason = "Blockerad Cloudflare Zaraz auto-injektion.";
  }

  return { vendor, reason, containerId };
}

function neutralizeScript(el: HTMLScriptElement, info: { vendor: BlockedScript["vendor"]; reason: string; containerId: string | null }) {
  const originalSrc = el.src || el.getAttribute("src") || "";

  // Stoppa exekvering: byt typ till något browsern inte kör.
  el.type = "text/x-blocked-analytics";
  // Tom data-URL → ingen nätverksbegäran om det redan satts på elementet.
  try {
    el.removeAttribute("src");
  } catch {
    /* noop */
  }
  el.setAttribute("data-blocked-by", "zooi-analytics-policy");
  el.setAttribute("data-blocked-vendor", info.vendor);
  el.setAttribute("data-original-src", originalSrc.slice(0, 500));

  // Om elementet redan finns i DOM, ta bort det helt så det inte ligger kvar
  // som "läckande" referens (bra för CSP-revisioner).
  try {
    el.parentNode?.removeChild(el);
  } catch {
    /* noop */
  }

  const record: BlockedScript = {
    src: originalSrc,
    vendor: info.vendor,
    containerId: info.containerId,
    reason: info.reason,
    blockedAt: new Date().toISOString(),
  };

  window.__zooiAnalyticsPolicyBlocks = window.__zooiAnalyticsPolicyBlocks || [];
  window.__zooiAnalyticsPolicyBlocks.push(record);

  // Dev-only telemetri: tydlig konsol-warning + CustomEvent som debugpanelen
  // (src/components/dev/AnalyticsPolicyDebugPanel.tsx) prenumererar på.
  // I prod är detta tyst — Discord-alerten i `maybeAlert` är produktionssignalen.
  try {
    if (import.meta.env?.DEV) {
       
      console.warn(
        `[analyticsScriptPolicy] 🛡️ blocked ${record.vendor}` +
          (record.containerId ? ` (${record.containerId})` : "") +
          ` — ${record.reason}`,
        { src: record.src, blockedAt: record.blockedAt },
      );
    }
    window.dispatchEvent(
      new CustomEvent<BlockedScript>("zooi:analytics-policy-block", { detail: record }),
    );
  } catch {
    /* noop */
  }

  // Rate-limited Discord-notis (best effort)
  void maybeAlert(record);
}

function inspectExistingScripts() {
  const scripts = Array.from(document.querySelectorAll("script[src]")) as HTMLScriptElement[];
  for (const s of scripts) {
    const verdict = evaluateScript(s);
    if (verdict) neutralizeScript(s, verdict);
  }
}

function attachMutationObserver() {
  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        const candidates: HTMLScriptElement[] = [];
        if (node.tagName === "SCRIPT") candidates.push(node as HTMLScriptElement);
        node.querySelectorAll?.("script[src]").forEach((s) =>
          candidates.push(s as HTMLScriptElement),
        );
        for (const s of candidates) {
          const verdict = evaluateScript(s);
          if (verdict) neutralizeScript(s, verdict);
        }
      });
    }
  });
  obs.observe(document.documentElement, { childList: true, subtree: true });
}

function patchCreateElement() {
  // Fångar `document.createElement('script')` + senare `.src = "..."`
  const origCreate = document.createElement.bind(document);
  document.createElement = function patchedCreateElement(
    tagName: string,
    options?: ElementCreationOptions,
  ): HTMLElement {
    const el = origCreate(tagName, options);
    if (tagName.toLowerCase() !== "script") return el;

    const script = el as HTMLScriptElement;
    let _src = "";
    Object.defineProperty(script, "src", {
      configurable: true,
      get() {
        return _src;
      },
      set(value: string) {
        _src = String(value || "");
        // Sätt även attributet så MutationObserver kan se det vid insertion
        try {
          script.setAttribute("src", _src);
        } catch {
          /* noop */
        }
        const verdict = evaluateScript(script);
        if (verdict) {
          // Block redan här — innan scriptet hinner sättas in i DOM
          neutralizeScript(script, verdict);
        }
      },
    });
    return script;
  } as typeof document.createElement;
}

function fingerprint(record: BlockedScript): string {
  return `${record.vendor}:${record.containerId ?? record.src.slice(0, 100)}`;
}

async function maybeAlert(record: BlockedScript) {
  if (!isTrackingEnabled()) return;
  try {
    const raw = window.localStorage.getItem(ALERT_LOCALSTORAGE_KEY);
    const fp = fingerprint(record);
    if (raw) {
      const { hash, ts } = JSON.parse(raw) as { hash: string; ts: number };
      if (hash === fp && Date.now() - ts < ALERT_COOLDOWN_MS) return;
    }
    window.localStorage.setItem(
      ALERT_LOCALSTORAGE_KEY,
      JSON.stringify({ hash: fp, ts: Date.now() }),
    );
  } catch {
    /* noop */
  }

  try {
    await supabase.functions.invoke("discord-notify", {
      body: {
        type: "raw_embed",
        data: {
          embeds: [
            {
              title: "🛡️ Analytics-policy: blockerade rogue script",
              description:
                "En okänd analytics-källa försökte ladda i browsern och blockerades innan exekvering.",
              color: 0xdc2626,
              fields: [
                { name: "Vendor", value: record.vendor, inline: true },
                { name: "Container", value: record.containerId ?? "—", inline: true },
                { name: "Hostname", value: window.location.hostname, inline: true },
                { name: "Reason", value: record.reason },
                { name: "Source URL", value: `\`${record.src.slice(0, 400)}\`` },
              ],
              timestamp: record.blockedAt,
              footer: { text: "src/lib/analyticsScriptPolicy.ts" },
            },
          ],
        },
      },
    });
  } catch {
    /* never break the app over a monitoring failure */
  }
}

/**
 * Installerar analytics script-policy. Idempotent och säker att kalla
 * upprepade gånger. Måste köras SÅ TIDIGT SOM MÖJLIGT i `main.tsx`
 * (före `bootstrapGoogleTracking`) för att hinna fånga tredjepartsinjektioner.
 */
export function installAnalyticsScriptPolicy(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.__zooiAnalyticsPolicyInstalled) return;
  window.__zooiAnalyticsPolicyInstalled = true;

  try {
    patchCreateElement();
    attachMutationObserver();
    // Sweep eventuella script som redan finns vid load (t.ex. SSR-injicerade)
    inspectExistingScripts();
  } catch {
    /* defensive — never break the page */
  }
}
